import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import toast from 'react-hot-toast';
import {
  ChevronLeft,
  ChevronRight,
  Add,
  Today,
} from '@mui/icons-material';
import { appointmentService } from '../../services/appointment.service';
import { patientService } from '../../services/patient.service';
import { dentistService } from '../../services/dentist.service';
import { treatmentService } from '../../services/treatment.service';
import { Appointment, Patient, Dentist, Treatment } from '../../types';

interface CalendarDay {
  date: Date;
  isCurrentMonth: boolean;
  isToday: boolean;
  appointments: Appointment[];
}

export default function CalendarPage() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarDays, setCalendarDays] = useState<CalendarDay[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  
  // Form data
  const [patients, setPatients] = useState<Patient[]>([]);
  const [dentists, setDentists] = useState<Dentist[]>([]);
  const [treatments, setTreatments] = useState<Treatment[]>([]);
  const [formData, setFormData] = useState({
    patientId: '',
    dentistId: '',
    treatmentId: '',
    startTime: '',
    endTime: '',
    notes: '',
  });
  const [formLoading, setFormLoading] = useState(false);

  const weekDays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
  const months = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  const generateCalendarDays = useCallback((date: Date, appointmentsList: Appointment[]) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - startDate.getDay());
    
    const days: CalendarDay[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    for (let i = 0; i < 42; i++) {
      const currentDay = new Date(startDate);
      currentDay.setDate(startDate.getDate() + i);
      
      const dayAppointments = appointmentsList.filter((apt) => {
        const aptDate = new Date(apt.startTime);
        return (
          aptDate.getDate() === currentDay.getDate() &&
          aptDate.getMonth() === currentDay.getMonth() &&
          aptDate.getFullYear() === currentDay.getFullYear()
        );
      });

      days.push({
        date: currentDay,
        isCurrentMonth: currentDay.getMonth() === month,
        isToday: currentDay.getTime() === today.getTime(),
        appointments: dayAppointments,
      });

      if (i >= 35 && currentDay > lastDay) break;
    }

    return days;
  }, []);

  const loadAppointments = useCallback(async () => {
    try {
      setLoading(true);
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const startDate = new Date(year, month, 1).toISOString().split('T')[0];
      const endDate = new Date(year, month + 1, 0).toISOString().split('T')[0];

      const data = await appointmentService.getAll({ startDate, endDate });
      setAppointments(data);
      setCalendarDays(generateCalendarDays(currentDate, data));
    } catch (error) {
      console.error('Error loading appointments:', error);
    } finally {
      setLoading(false);
    }
  }, [currentDate, generateCalendarDays]);

  useEffect(() => {
    loadAppointments();
  }, [loadAppointments]);

  useEffect(() => {
    const loadFormData = async () => {
      try {
        const [patientsData, dentistsData, treatmentsData] = await Promise.all([
          patientService.getAll(),
          dentistService.getAll(),
          treatmentService.getAll(),
        ]);
        setPatients(Array.isArray(patientsData) ? patientsData : patientsData.data || []);
        setDentists(Array.isArray(dentistsData) ? dentistsData : dentistsData.data || []);
        setTreatments(Array.isArray(treatmentsData) ? treatmentsData : treatmentsData.data || []);
      } catch (error) {
        console.error('Error loading form data:', error);
      }
    };
    loadFormData();
  }, []);

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const handleDayClick = (day: CalendarDay) => {
    setSelectedDate(day.date);
    const dateStr = day.date.toISOString().split('T')[0];
    setFormData({
      ...formData,
      startTime: `${dateStr}T09:00`,
      endTime: `${dateStr}T10:00`,
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedDate(null);
    setFormData({
      patientId: '',
      dentistId: '',
      treatmentId: '',
      startTime: '',
      endTime: '',
      notes: '',
    });
  };

  const handleSubmit = async () => {
    if (!formData.patientId || !formData.dentistId || !formData.startTime || !formData.endTime) {
      toast.error('Preencha paciente, dentista e horários');
      return;
    }

    try {
      setFormLoading(true);
      await appointmentService.create({
        patientId: formData.patientId,
        dentistId: formData.dentistId,
        treatmentId: formData.treatmentId || undefined,
        startTime: new Date(formData.startTime).toISOString(),
        endTime: new Date(formData.endTime).toISOString(),
        notes: formData.notes || undefined,
      });
      handleCloseDialog();
      loadAppointments();
      toast.success('Consulta cadastrada com sucesso');
    } catch (error) {
      console.error('Error creating appointment:', error);
      toast.error('Não foi possível cadastrar a consulta');
    } finally {
      setFormLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, 'primary' | 'success' | 'error' | 'warning' | 'default'> = {
      scheduled: 'primary',
      confirmed: 'success',
      completed: 'default',
      cancelled: 'error',
      no_show: 'warning',
    };
    return colors[status] || 'default';
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight="bold">
          Calendário
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => {
            setSelectedDate(new Date());
            const today = new Date().toISOString().split('T')[0];
            setFormData({
              ...formData,
              startTime: `${today}T09:00`,
              endTime: `${today}T10:00`,
            });
            setDialogOpen(true);
          }}
        >
          Nova Consulta
        </Button>
      </Box>

      <Paper elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, overflow: 'hidden' }}>
        {/* Calendar Header */}
        <Box
          sx={{
            p: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid #e0e0e0',
            backgroundColor: 'grey.50',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton onClick={handlePrevMonth}>
              <ChevronLeft />
            </IconButton>
            <IconButton onClick={handleNextMonth}>
              <ChevronRight />
            </IconButton>
            <Typography variant="h6" fontWeight="600" sx={{ ml: 1 }}>
              {months[currentDate.getMonth()]} {currentDate.getFullYear()}
            </Typography>
          </Box>
          <Button variant="outlined" startIcon={<Today />} onClick={handleToday}>
            Hoje
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Week Days Header */}
            <Grid container sx={{ borderBottom: '1px solid #e0e0e0' }}>
              {weekDays.map((day) => (
                <Grid
                  item
                  key={day}
                  xs={12 / 7}
                  sx={{
                    p: 1,
                    textAlign: 'center',
                    fontWeight: 600,
                    color: 'text.secondary',
                    backgroundColor: 'grey.50',
                  }}
                >
                  {day}
                </Grid>
              ))}
            </Grid>

            {/* Calendar Days */}
            <Grid container>
              {calendarDays.map((day, index) => (
                <Grid
                  item
                  key={index}
                  xs={12 / 7}
                  sx={{
                    minHeight: 100,
                    p: 0.5,
                    borderRight: (index + 1) % 7 !== 0 ? '1px solid #e0e0e0' : 'none',
                    borderBottom: '1px solid #e0e0e0',
                    backgroundColor: day.isToday
                      ? 'primary.50'
                      : !day.isCurrentMonth
                      ? 'grey.100'
                      : 'white',
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: day.isToday ? 'primary.100' : 'grey.50',
                    },
                  }}
                  onClick={() => handleDayClick(day)}
                >
                  <Typography
                    sx={{
                      p: 0.5,
                      width: 28,
                      height: 28,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '50%',
                      fontWeight: day.isToday ? 700 : 400,
                      color: !day.isCurrentMonth
                        ? 'text.disabled'
                        : day.isToday
                        ? 'white'
                        : 'text.primary',
                      backgroundColor: day.isToday ? 'primary.main' : 'transparent',
                    }}
                  >
                    {day.date.getDate()}
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    {day.appointments.slice(0, 3).map((apt) => (
                      <Chip
                        key={apt.id}
                        label={`${formatTime(apt.startTime)} - ${apt.patient?.name?.split(' ')[0] || 'Paciente'}`}
                        size="small"
                        color={getStatusColor(apt.status)}
                        sx={{
                          width: '100%',
                          mb: 0.5,
                          height: 20,
                          fontSize: '0.7rem',
                          justifyContent: 'flex-start',
                        }}
                      />
                    ))}
                    {day.appointments.length > 3 && (
                      <Typography variant="caption" color="text.secondary" sx={{ pl: 0.5 }}>
                        +{day.appointments.length - 3} mais
                      </Typography>
                    )}
                  </Box>
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </Paper>

      {/* New Appointment Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Nova Consulta
          {selectedDate && (
            <Typography variant="body2" color="text.secondary">
              {selectedDate.toLocaleDateString('pt-BR', {
                weekday: 'long',
                day: '2-digit',
                month: 'long',
                year: 'numeric',
              })}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Autocomplete
              options={patients}
              getOptionLabel={(option) => option.name}
              onChange={(_, value) => setFormData({ ...formData, patientId: value?.id || '' })}
              renderInput={(params) => (
                <TextField {...params} label="Paciente" required fullWidth margin="normal" />
              )}
            />

            <FormControl fullWidth margin="normal" required>
              <InputLabel>Dentista</InputLabel>
              <Select
                value={formData.dentistId}
                onChange={(e) => setFormData({ ...formData, dentistId: e.target.value })}
                label="Dentista"
              >
                {dentists.map((dentist) => (
                  <MenuItem key={dentist.id} value={dentist.id}>
                    {dentist.user?.name || 'Dentista'} - {dentist.specialty}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth margin="normal">
              <InputLabel>Tratamento</InputLabel>
              <Select
                value={formData.treatmentId}
                onChange={(e) => setFormData({ ...formData, treatmentId: e.target.value })}
                label="Tratamento"
              >
                <MenuItem value="">Nenhum</MenuItem>
                {treatments.map((treatment) => (
                  <MenuItem key={treatment.id} value={treatment.id}>
                    {treatment.name} - R$ {treatment.price.toFixed(2)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Início"
                  type="datetime-local"
                  value={formData.startTime}
                  onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                  margin="normal"
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Fim"
                  type="datetime-local"
                  value={formData.endTime}
                  onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                  margin="normal"
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
            </Grid>

            <TextField
              fullWidth
              label="Observações"
              multiline
              rows={3}
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              margin="normal"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!formData.patientId || !formData.dentistId || !formData.startTime || !formData.endTime || formLoading}
          >
            {formLoading ? <CircularProgress size={24} /> : 'Agendar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
